import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from '@tanstack/react-router'
import { mdiArchiveOutline, mdiBackupRestore, mdiCogOutline, mdiDelete } from '@mdi/js'
import { useBreadcrumbs } from '../../hooks/useBreadcrumbs'
import { usePermissions } from '../../contexts/PermissionsContext'
import { useAssetTypes, useDeleteDocument, useDocumentTypes, useRestoreAsset } from '../../hooks/useQueries'
import {
    typeLabel,
    useArchivedItems,
    useInvalidateArchive,
    useRestoreDocument,
    useSetStaleAfterDays,
    type ArchiveBatchFilter,
    type ArchivedAsset,
    type ArchivedDocument,
    type ArchivedKind,
} from '../../hooks/use-archive'
import { PageHeader } from '../shared/PageHeader'
import { ArchivedMarker } from '../ArchivedMarker'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Checkbox } from '../ui/checkbox'
import { Badge } from '../ui/badge'
import { Label } from '../ui/label'
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover'
import { AppIcon } from '../ui/icon'
import { EmptyState } from '../ui/empty-state'
import { DeleteConfirmDialog } from '../ui/delete-confirm-dialog'
import { Tabs, TabsList, TabsTrigger } from '../ui/tabs'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table'
import { ArchiveDeleteDialog, ArchiveBulkDeleteDialog, type BatchSelection } from './ArchiveDeleteDialog'

const PAGE_SIZE = 20

/** The selection of the active tab, shared by the two tables. */
export type SelectionState = {
    selected: string[]
    setSelected: (updater: string[] | ((prev: string[]) => string[])) => void
    allMatching: boolean
    setAllMatching: (on: boolean) => void
    total: number
    filter: ArchiveBatchFilter
    openBulkDelete: () => void
}

/** Notify shows a non-blocking status line above the table. */
export type Notify = (message: string, tone?: 'ok' | 'error') => void

/**
 * What the page lists, kept in the URL so a filtered view survives a reload
 * and can be shared (issue #1522). Defaults are left out of the URL.
 * `aged` turns on "archived for more than N days"; `days` is N when it differs
 * from the organization's stale setting.
 */
export type ArchiveSearch = {
    tab?: 'documents'
    q?: string
    type?: string
    sort?: 'oldest'
    page?: number
    aged?: true
    days?: number
}

const positiveInt = (v: unknown): number | undefined => {
    const n = typeof v === 'number' ? v : typeof v === 'string' ? parseInt(v, 10) : NaN
    return Number.isInteger(n) && n > 0 ? n : undefined
}

/** validateSearch of the archive route: unknown or default values are dropped. */
export function parseArchiveSearch(search: Record<string, unknown>): ArchiveSearch {
    const page = positiveInt(search.page)
    return {
        tab: search.tab === 'documents' ? 'documents' : undefined,
        q: typeof search.q === 'string' && search.q ? search.q : undefined,
        type: typeof search.type === 'string' && search.type ? search.type : undefined,
        sort: search.sort === 'oldest' ? 'oldest' : undefined,
        page: page && page > 1 ? page : undefined,
        aged: search.aged === true || search.aged === 'true' ? true : undefined,
        days: positiveInt(search.days),
    }
}

/**
 * The archived-items review page (spec 2026-10-08-02): every archived asset
 * subtree and every archived document of the organization, with Restore and a
 * Delete that previews what goes with the subtree. Owner and admin only
 * (`archive.read`, deleting needs `archive.write`).
 */
export function ArchivedItemsPage({ org, search, onSearchChange }: {
    org: string
    search: ArchiveSearch
    /** Writes the next search to the URL; `replace` for keystrokes. */
    onSearchChange: (next: ArchiveSearch, options?: { replace?: boolean }) => void
}) {
    const { t } = useTranslation('archive')
    const [staleDays, setStaleDays] = useState(90)
    const kind: ArchivedKind = search.tab ?? 'assets'
    const query = search.q ?? ''
    const type = search.type ?? ''
    const ascending = search.sort === 'oldest'
    const page = search.page ?? 1
    // Age filter: "archived for more than N days", N prefilled with the stale setting.
    const ageOn = search.aged === true
    const ageDays = search.days ?? null
    const setPage = (next: number) => onSearchChange({ ...search, page: next > 1 ? next : undefined })
    // Selection: uids across pages, or every item matching the filter.
    const [selected, setSelected] = useState<string[]>([])
    const [allMatching, setAllMatching] = useState(false)
    const [bulkOpen, setBulkOpen] = useState(false)
    const [notice, setNotice] = useState<{ message: string; tone: 'ok' | 'error' } | null>(null)
    const notify: Notify = (message, tone = 'ok') => setNotice({ message, tone })

    const breadcrumbs = useMemo(
        () => [
            { title: t('common:nav.home'), path: `/${org}` },
            { title: t('settings:page.title'), path: `/${org}/settings` },
            { title: t('breadcrumb') },
        ],
        [org, t]
    )
    useBreadcrumbs(breadcrumbs)

    // The instant behind a custom age; fixed per N so the query key stays stable.
    const effectiveDays = ageDays ?? staleDays
    const archivedBefore = useMemo(
        () => (ageOn ? new Date(Date.now() - effectiveDays * 86_400_000).toISOString() : undefined),
        [ageOn, effectiveDays]
    )
    const useStale = ageOn && effectiveDays === staleDays

    const list = useArchivedItems(org, {
        kind,
        q: query,
        type,
        sort: ascending ? 'archived_at' : '-archived_at',
        stale: useStale,
        archivedBefore: useStale ? undefined : archivedBefore,
        page,
        limit: PAGE_SIZE,
    })
    const totalPages = list.data?.total_pages ?? 1
    const total = list.data?.total ?? 0
    const serverStaleDays = list.data?.stale_after_days
    useEffect(() => {
        if (serverStaleDays) setStaleDays(serverStaleDays)
    }, [serverStaleDays])

    const filter: ArchiveBatchFilter = {
        q: query || undefined,
        type: type || undefined,
        ...(useStale ? { stale: true } : archivedBefore ? { archived_before: archivedBefore } : {}),
    }

    // Any change of what is listed drops "all matching", which was resolved against the old filter.
    const resetSelection = () => {
        setSelected([])
        setAllMatching(false)
    }
    // A filter change goes back to page 1.
    const changeFilter = (patch: Partial<ArchiveSearch>, options?: { replace?: boolean }) => {
        onSearchChange({ ...search, ...patch, page: undefined }, options)
        resetSelection()
    }

    const switchKind = (next: string) => {
        changeFilter({ tab: next === 'documents' ? 'documents' : undefined, type: undefined })
    }

    const selection: SelectionState = {
        selected,
        setSelected,
        allMatching,
        setAllMatching,
        total,
        filter,
        openBulkDelete: () => setBulkOpen(true),
    }
    // The dialog lives on the page: the tables unmount once the deleted rows are gone.
    const batchSelection: BatchSelection = allMatching ? { filter } : { uids: selected }

    return (
        <div className="container mx-auto py-8 max-w-6xl space-y-6" data-testid="archive-page">
            <PageHeader
                icon={mdiArchiveOutline}
                title={t('title')}
                description={t('subtitle')}
                actions={<StaleSettings org={org} days={staleDays} />}
            />

            <Tabs value={kind} onValueChange={switchKind}>
                <TabsList>
                    <TabsTrigger value="assets" data-testid="archive-tab-assets">
                        {t('tabs.assets')}
                    </TabsTrigger>
                    <TabsTrigger value="documents" data-testid="archive-tab-documents">
                        {t('tabs.documents')}
                    </TabsTrigger>
                </TabsList>
            </Tabs>

            <div className="flex flex-wrap items-center gap-3">
                <Input
                    className="max-w-xs"
                    value={query}
                    placeholder={t('search_placeholder')}
                    onChange={(e) => changeFilter({ q: e.target.value || undefined }, { replace: true })}
                    data-testid="archive-search"
                />
                <TypeFilter org={org} kind={kind} value={type} onChange={(v) => changeFilter({ type: v || undefined })} />
                <div className="flex items-center gap-2 text-sm">
                    <Checkbox
                        checked={ageOn}
                        aria-label={t('age.enable')}
                        onCheckedChange={(on) => changeFilter({ aged: on ? true : undefined })}
                        data-testid="archive-age-enabled"
                    />
                    <span>{t('age.prefix')}</span>
                    <Input
                        type="number"
                        min={1}
                        className="w-20"
                        value={effectiveDays}
                        onChange={(e) => {
                            const n = parseInt(e.target.value, 10)
                            changeFilter({ days: Number.isFinite(n) && n > 0 ? n : undefined }, { replace: true })
                        }}
                        data-testid="archive-age-days"
                    />
                    <span>{t('age.suffix')}</span>
                </div>
                <select
                    className="h-9 rounded-md border border-border bg-background px-2 text-sm"
                    value={ascending ? 'asc' : 'desc'}
                    onChange={(e) => onSearchChange({ ...search, sort: e.target.value === 'asc' ? 'oldest' : undefined })}
                    data-testid="archive-sort"
                >
                    <option value="desc">{t('sort_newest')}</option>
                    <option value="asc">{t('sort_oldest')}</option>
                </select>
            </div>

            {notice && (
                <div
                    role="status"
                    className={`rounded-md border p-3 text-sm ${notice.tone === 'error' ? 'border-destructive/20 bg-destructive/10 text-destructive' : 'border-border bg-muted/40'}`}
                    data-testid="archive-notice"
                >
                    {notice.message}
                </div>
            )}
            {list.error && (
                <div className="bg-destructive/10 border border-destructive/20 rounded-md p-4 text-destructive text-sm" data-testid="archive-error">
                    {t('load_error')}
                </div>
            )}
            {list.isLoading && <div className="text-muted-foreground">{t('loading')}</div>}
            {list.data && (
                <div className="text-sm text-muted-foreground" data-testid="archive-count">
                    {ageOn
                        ? t('age.count', { count: total, days: effectiveDays })
                        : t('count', { count: total })}
                </div>
            )}

            {list.data && kind === 'assets' && (
                <AssetsTable org={org} notify={notify} selection={selection} rows={(list.data.data ?? []) as ArchivedAsset[]} />
            )}
            {list.data && kind === 'documents' && (
                <DocumentsTable org={org} notify={notify} selection={selection} rows={(list.data.data ?? []) as ArchivedDocument[]} />
            )}

            {bulkOpen && (
                <ArchiveBulkDeleteDialog
                    org={org}
                    kind={kind}
                    selection={batchSelection}
                    notify={notify}
                    onClose={(done) => {
                        setBulkOpen(false)
                        if (done) resetSelection()
                    }}
                />
            )}

            {totalPages > 1 && (
                <div className="flex items-center justify-end gap-3 text-sm" data-testid="archive-pagination">
                    <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="archive-prev">
                        {t('actions.previous')}
                    </Button>
                    <span>{t('page_of', { page, pages: totalPages })}</span>
                    <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(page + 1)} data-testid="archive-next">
                        {t('actions.next')}
                    </Button>
                </div>
            )}
        </div>
    )
}

function TypeFilter({ org, kind, value, onChange }: { org: string; kind: ArchivedKind; value: string; onChange: (v: string) => void }) {
    const { t, i18n } = useTranslation('archive')
    const assetTypes = useAssetTypes(org)
    const documentTypes = useDocumentTypes(org)
    const options = (kind === 'assets' ? assetTypes.data : documentTypes.data) ?? []
    return (
        <select
            className="h-9 rounded-md border border-border bg-background px-2 text-sm"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            data-testid="archive-type-filter"
        >
            <option value="">{t('type_all')}</option>
            {options
                .filter((o: { code?: string }) => !!o.code)
                .map((o: { code?: string; name?: unknown }) => (
                    <option key={o.code} value={o.code}>
                        {typeLabel(o.name as Record<string, string> | undefined, o.code, i18n.language)}
                    </option>
                ))}
        </select>
    )
}

function formatDate(value: string, language: string) {
    return new Date(value).toLocaleDateString(language)
}

function AssetsTable({ org, rows, notify, selection }: { org: string; rows: ArchivedAsset[]; notify: Notify; selection: SelectionState }) {
    const { t, i18n } = useTranslation('archive')
    const { permissions } = usePermissions()
    const canDelete = permissions.includes('archive.write')
    const restore = useRestoreAsset(org)
    const refreshArchive = useInvalidateArchive()
    const { selected, setSelected } = selection
    const [deleting, setDeleting] = useState<ArchivedAsset | null>(null)
    const allSelected = rows.length > 0 && rows.every((r) => selected.includes(r.uid))

    const doRestore = async (uids: string[]) => {
        try {
            for (const uid of uids) await restore.mutateAsync({ assetUid: uid })
            refreshArchive()
            setSelected([])
            selection.setAllMatching(false)
            notify(t('restored'))
        } catch {
            notify(t('errors.generic'), 'error')
        }
    }

    if (rows.length === 0) {
        return <EmptyState title={t('empty.assets_title')} description={t('empty.assets_description')} data-testid="archive-assets-empty" />
    }

    return (
        <div className="space-y-3">
            <SelectionBar
                rows={rows}
                selection={selection}
                canDelete={canDelete}
                onRestore={doRestore}
                restoring={restore.isPending}
            />
            <div className="bg-card rounded-lg border border-border">
                <Table data-testid="archive-assets-table">
                    <TableHeader>
                        <TableRow>
                            <TableHead className="w-8">
                                <Checkbox
                                    checked={allSelected}
                                    aria-label={t('actions.select_all')}
                                    onCheckedChange={(on) => {
                                        setSelected(on ? Array.from(new Set([...selected, ...rows.map((r) => r.uid)])) : selected.filter((u) => !rows.some((r) => r.uid === u)))
                                        if (!on) selection.setAllMatching(false)
                                    }}
                                    data-testid="archive-select-all"
                                />
                            </TableHead>
                            <TableHead>{t('columns.name')}</TableHead>
                            <TableHead>{t('columns.type')}</TableHead>
                            <TableHead>{t('columns.archived')}</TableHead>
                            <TableHead>{t('columns.archived_by')}</TableHead>
                            <TableHead className="text-right">{t('columns.descendants')}</TableHead>
                            <TableHead className="text-right">{t('columns.documents')}</TableHead>
                            <TableHead className="text-right">{t('columns.actions')}</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {rows.map((row) => {
                            const name = row.name || row.uid.slice(0, 8)
                            return (
                                <TableRow key={row.uid} data-testid={`archive-asset-row-${row.uid}`}>
                                    <TableCell>
                                        <Checkbox
                                            checked={selected.includes(row.uid)}
                                            aria-label={t('actions.select_row', { name })}
                                            onCheckedChange={(on) => {
                                                setSelected((prev) => (on ? [...prev, row.uid] : prev.filter((u) => u !== row.uid)))
                                                if (!on) selection.setAllMatching(false)
                                            }}
                                            data-testid={`archive-select-${row.uid}`}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <div className="flex items-center gap-2">
                                            <Link
                                                to="/$org/assets/$assetUid"
                                                params={{ org, assetUid: row.uid }}
                                                className="font-medium hover:underline"
                                                data-testid={`archive-asset-name-${row.uid}`}
                                            >
                                                {name}
                                            </Link>
                                            <ArchivedMarker archivedAt={row.archived_at} testId={`archive-marker-${row.uid}`} />
                                            {row.stale && <StaleBadge testId={`archive-stale-${row.uid}`} />}
                                        </div>
                                        {row.parent_name && <div className="text-xs text-muted-foreground">{row.parent_name}</div>}
                                    </TableCell>
                                    <TableCell>{typeLabel(row.type_name, row.type_code, i18n.language)}</TableCell>
                                    <TableCell>{formatDate(row.archived_at, i18n.language)}</TableCell>
                                    <TableCell>{row.archived_by?.name || row.archived_by?.email || ''}</TableCell>
                                    <TableCell className="text-right" data-testid={`archive-descendants-${row.uid}`}>
                                        {row.descendant_count}
                                    </TableCell>
                                    <TableCell className="text-right" data-testid={`archive-documents-${row.uid}`}>
                                        {row.document_count}
                                    </TableCell>
                                    <TableCell className="text-right">
                                        <div className="flex justify-end gap-2">
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={() => doRestore([row.uid])}
                                                disabled={restore.isPending}
                                                data-testid={`archive-restore-${row.uid}`}
                                            >
                                                {t('actions.restore')}
                                            </Button>
                                            {canDelete && (
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    className="text-destructive hover:text-destructive/80"
                                                    onClick={() => setDeleting(row)}
                                                    data-testid={`archive-delete-${row.uid}`}
                                                >
                                                    {t('actions.delete')}
                                                </Button>
                                            )}
                                        </div>
                                    </TableCell>
                                </TableRow>
                            )
                        })}
                    </TableBody>
                </Table>
            </div>
            {deleting && <ArchiveDeleteDialog org={org} asset={deleting} notify={notify} onClose={() => setDeleting(null)} />}
        </div>
    )
}

function DocumentsTable({ org, rows, notify, selection }: { org: string; rows: ArchivedDocument[]; notify: Notify; selection: SelectionState }) {
    const { t, i18n } = useTranslation('archive')
    const restore = useRestoreDocument(org)
    const remove = useDeleteDocument(org)
    const { permissions } = usePermissions()
    const canDelete = permissions.includes('archive.write')
    const { selected, setSelected } = selection
    const allSelected = rows.length > 0 && rows.every((r) => selected.includes(r.uid))

    if (rows.length === 0) {
        return <EmptyState title={t('empty.documents_title')} description={t('empty.documents_description')} data-testid="archive-documents-empty" />
    }

    const doRestore = async (uid: string) => {
        try {
            await restore.mutateAsync(uid)
            notify(t('document_restored'))
        } catch {
            notify(t('errors.generic'), 'error')
        }
    }

    return (
        <div className="space-y-3">
        <SelectionBar rows={rows} selection={selection} canDelete={canDelete} />
        <div className="bg-card rounded-lg border border-border">
            <Table data-testid="archive-documents-table">
                <TableHeader>
                    <TableRow>
                        <TableHead className="w-8">
                            <Checkbox
                                checked={allSelected}
                                aria-label={t('actions.select_all')}
                                onCheckedChange={(on) => {
                                    setSelected(on ? Array.from(new Set([...selected, ...rows.map((r) => r.uid)])) : selected.filter((u) => !rows.some((r) => r.uid === u)))
                                    if (!on) selection.setAllMatching(false)
                                }}
                                data-testid="archive-select-all"
                            />
                        </TableHead>
                        <TableHead>{t('columns.name')}</TableHead>
                        <TableHead>{t('columns.type')}</TableHead>
                        <TableHead>{t('columns.asset')}</TableHead>
                        <TableHead>{t('columns.archived')}</TableHead>
                        <TableHead>{t('columns.archived_by')}</TableHead>
                        <TableHead className="text-right">{t('columns.actions')}</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {rows.map((row) => (
                        <TableRow key={row.uid} data-testid={`archive-document-row-${row.uid}`}>
                            <TableCell>
                                <Checkbox
                                    checked={selected.includes(row.uid)}
                                    aria-label={t('actions.select_row', { name: row.name || row.file_name })}
                                    onCheckedChange={(on) => {
                                        setSelected((prev) => (on ? [...prev, row.uid] : prev.filter((u) => u !== row.uid)))
                                        if (!on) selection.setAllMatching(false)
                                    }}
                                    data-testid={`archive-select-${row.uid}`}
                                />
                            </TableCell>
                            <TableCell>
                                <div className="flex items-center gap-2">
                                    <Link
                                        to="/$org/documents/$docUid"
                                        params={{ org, docUid: row.uid }}
                                        className="font-medium hover:underline"
                                    >
                                        {row.name || row.file_name}
                                    </Link>
                                    <ArchivedMarker archivedAt={row.archived_at} testId={`archive-document-marker-${row.uid}`} />
                                    {row.stale && <StaleBadge testId={`archive-stale-${row.uid}`} />}
                                </div>
                            </TableCell>
                            <TableCell>{typeLabel(row.type_name, row.type_code, i18n.language)}</TableCell>
                            <TableCell>{row.asset_name ?? ''}</TableCell>
                            <TableCell>{formatDate(row.archived_at, i18n.language)}</TableCell>
                            <TableCell>{row.archived_by?.name || row.archived_by?.email || ''}</TableCell>
                            <TableCell className="text-right">
                                <div className="flex justify-end gap-2">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => doRestore(row.uid)}
                                        disabled={restore.isPending}
                                        data-testid={`archive-document-restore-${row.uid}`}
                                    >
                                        {t('actions.restore')}
                                    </Button>
                                    <DeleteConfirmDialog
                                        trigger={
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                className="text-destructive hover:text-destructive/80"
                                                data-testid={`archive-document-delete-${row.uid}`}
                                            >
                                                {t('actions.delete')}
                                            </Button>
                                        }
                                        title={t('confirm_delete_document_title')}
                                        description={t('confirm_delete_document_description')}
                                        isDeleting={remove.isPending}
                                        onConfirm={async () => {
                                            try {
                                                await remove.mutateAsync(row.uid)
                                                notify(t('deleted'))
                                            } catch {
                                                notify(t('errors.generic'), 'error')
                                            }
                                        }}
                                    />
                                </div>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </div>
        </div>
    )
}

function StaleBadge({ testId }: { testId: string }) {
    const { t } = useTranslation('archive')
    return (
        <Badge variant="warning" data-testid={testId}>
            {t('stale')}
        </Badge>
    )
}

/**
 * The bulk bar of a tab: what is selected, the "select all N matching" switch
 * (the selection then follows the filter instead of uids), and the delete
 * that opens the batch preview dialog.
 */
function SelectionBar({
    rows,
    selection,
    canDelete,
    onRestore,
    restoring,
}: {
    rows: { uid: string }[]
    selection: SelectionState
    canDelete: boolean
    onRestore?: (uids: string[]) => void
    restoring?: boolean
}) {
    const { t } = useTranslation('archive')
    const { selected, setSelected, allMatching, setAllMatching, total } = selection
    const pageAllSelected = rows.length > 0 && rows.every((r) => selected.includes(r.uid))
    const count = allMatching ? total : selected.length
    if (count === 0) return null

    return (
        <div className="space-y-2">
            <div className="flex items-center gap-3 text-sm" data-testid="archive-bulk-bar">
                <span data-testid="archive-selected-count">{t('selected', { count })}</span>
                {onRestore && !allMatching && (
                    <Button variant="outline" size="sm" disabled={restoring} onClick={() => onRestore(selected)} data-testid="archive-bulk-restore">
                        <AppIcon path={mdiBackupRestore} size="sm" className="mr-1" />
                        {t('actions.restore_selected')}
                    </Button>
                )}
                {canDelete && (
                    <Button variant="destructive" size="sm" onClick={selection.openBulkDelete} data-testid="archive-bulk-delete">
                        <AppIcon path={mdiDelete} size="sm" className="mr-1" />
                        {t('actions.delete_selected')}
                    </Button>
                )}
            </div>
            {pageAllSelected && !allMatching && total > selected.length && (
                <div className="text-sm" data-testid="archive-select-all-banner">
                    {t('select_all_page', { count: rows.length })}{' '}
                    <button type="button" className="font-medium underline" onClick={() => setAllMatching(true)} data-testid="archive-select-all-matching">
                        {t('select_all_matching', { count: total })}
                    </button>
                </div>
            )}
            {allMatching && (
                <div className="text-sm" data-testid="archive-all-matching-banner">
                    {t('all_matching_selected', { count: total })}{' '}
                    <button
                        type="button"
                        className="font-medium underline"
                        onClick={() => {
                            setAllMatching(false)
                            setSelected([])
                        }}
                        data-testid="archive-clear-selection"
                    >
                        {t('clear_selection')}
                    </button>
                </div>
            )}
        </div>
    )
}

/** A popover on the page header that writes `archive.stale_after_days`. */
function StaleSettings({ org, days }: { org: string; days: number }) {
    const { t } = useTranslation('archive')
    const { permissions } = usePermissions()
    const save = useSetStaleAfterDays(org)
    const [value, setValue] = useState(String(days))
    const [open, setOpen] = useState(false)
    const [error, setError] = useState(false)
    if (!permissions.includes('archive.write')) return null
    const n = parseInt(value, 10)
    return (
        <Popover
            open={open}
            onOpenChange={(o) => {
                setOpen(o)
                if (o) {
                    setValue(String(days))
                    setError(false)
                }
            }}
        >
            <PopoverTrigger asChild>
                <Button variant="outline" size="sm" data-testid="archive-settings-trigger">
                    <AppIcon path={mdiCogOutline} size="sm" className="mr-1" />
                    {t('settings.trigger')}
                </Button>
            </PopoverTrigger>
            <PopoverContent className="w-72 space-y-2" data-testid="archive-settings-popover">
                <Label htmlFor="archive-stale-days">{t('settings.label')}</Label>
                <Input
                    id="archive-stale-days"
                    type="number"
                    min={1}
                    max={3650}
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    data-testid="archive-stale-days-input"
                />
                <p className="text-xs text-muted-foreground">{t('settings.help')}</p>
                {error && (
                    <p className="text-xs text-destructive" role="alert" data-testid="archive-settings-error">
                        {t('errors.generic')}
                    </p>
                )}
                <Button
                    size="sm"
                    disabled={!(n >= 1 && n <= 3650) || save.isPending}
                    onClick={async () => {
                        try {
                            await save.mutateAsync(n)
                            setOpen(false)
                        } catch {
                            setError(true)
                        }
                    }}
                    data-testid="archive-stale-days-save"
                >
                    {t('settings.save')}
                </Button>
            </PopoverContent>
        </Popover>
    )
}
